import React, { useState, useEffect } from "react";
import {
  Container,
  Table,
  Button,
  Badge,
  Form,
  Row,
  Col,
  Modal,
  Spinner,
  Alert,
  Tabs,
  Tab,
} from "react-bootstrap";
import {
  getReservations,
  updateReservationStatus,
  deleteReservation,
  getDiabetesCareRegistrations,
  updateDiabetesCareStatus,
  deleteDiabetesCareRegistration,
} from "../services/firebaseService";
import { reservationStatus } from "../models/models";
import AvailabilityManager from "./AvailabilityManager";
import { useTranslation } from "react-i18next";

const AdminDashboard = () => {
  const [reservations, setReservations] = useState([]);
  const [diabetesCareRegistrations, setDiabetesCareRegistrations] = useState(
    []
  );
  const [loading, setLoading] = useState(true);
  const [diabetesCareLoading, setDiabetesCareLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [diabetesStatusFilter, setDiabetesStatusFilter] = useState("all");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [actionType, setActionType] = useState("");
  const [activeTab, setActiveTab] = useState("reservations");
  const { t } = useTranslation();

  useEffect(() => {
    fetchReservations();
    fetchDiabetesCareRegistrations();
  }, []);

  const fetchReservations = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getReservations();
      setReservations(data);
    } catch (err) {
      console.error("Error fetching reservations:", err);
      setError("Failed to load reservations. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchDiabetesCareRegistrations = async () => {
    try {
      setDiabetesCareLoading(true);
      const data = await getDiabetesCareRegistrations();
      setDiabetesCareRegistrations(data);
    } catch (err) {
      console.error("Error fetching diabetes care registrations:", err);
      setError("Failed to load diabetes care registrations. Please try again.");
    } finally {
      setDiabetesCareLoading(false);
    }
  };

  const handleStatusChange = async (reservation, newStatus) => {
    setSelectedReservation(reservation);
    setActionType("status-" + newStatus);
    setShowConfirmModal(true);
  };

  const handleDeleteReservation = (reservation) => {
    setSelectedReservation(reservation);
    setActionType("delete");
    setShowConfirmModal(true);
  };

  // Diabetes care handlers
  const handleDiabetesCareStatusChange = async (registration, newStatus) => {
    setSelectedReservation(registration);
    setActionType("diabetes-status-" + newStatus);
    setShowConfirmModal(true);
  };

  const handleDeleteDiabetesCareRegistration = (registration) => {
    setSelectedReservation(registration);
    setActionType("diabetes-delete");
    setShowConfirmModal(true);
  };

  const confirmAction = async () => {
    setShowConfirmModal(false);

    try {
      if (actionType === "delete") {
        await deleteReservation(selectedReservation.id);
        fetchReservations();
      } else if (actionType.startsWith("status-")) {
        const newStatus = actionType.split("-")[1];
        await updateReservationStatus(selectedReservation.id, newStatus);
        fetchReservations();
      } else if (actionType === "diabetes-delete") {
        await deleteDiabetesCareRegistration(selectedReservation.id);
        fetchDiabetesCareRegistrations();
      } else if (actionType.startsWith("diabetes-status-")) {
        const newStatus = actionType.split("-")[2];
        await updateDiabetesCareStatus(selectedReservation.id, newStatus);
        fetchDiabetesCareRegistrations();
      }
    } catch (err) {
      console.error("Error performing action:", err);
      setError(
        `Failed to ${
          actionType.includes("status")
            ? "update status"
            : "delete registration"
        }. Please try again.`
      );
    }
  };

  const filteredReservations =
    statusFilter === "all"
      ? reservations
      : reservations.filter((r) => r.status === statusFilter);

  const filteredDiabetesCareRegistrations =
    diabetesStatusFilter === "all"
      ? diabetesCareRegistrations
      : diabetesCareRegistrations.filter(
          (r) => r.status === diabetesStatusFilter
        );

  const getStatusBadge = (status) => {
    switch (status) {
      case reservationStatus.PENDING:
        return (
          <Badge bg="warning" text="dark">
            {t("Pending")}
          </Badge>
        );
      case reservationStatus.FULFILLED:
        return <Badge bg="success">{t("Fulfilled")}</Badge>;
      case reservationStatus.DENIED:
        return <Badge bg="danger">{t("Denied")}</Badge>;
      case reservationStatus.CANCELLED:
        return <Badge bg="secondary">{t("Cancelled")}</Badge>;
      default:
        return (
          <Badge bg="light" text="dark">
            {status}
          </Badge>
        );
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString();
  };

  return (
    <Container className="my-5">
      <h2 className="mb-4">{t("DoctorVisitAdmin")}</h2>

      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        className="mb-4"
      >
        <Tab eventKey="reservations" title={t("ManageReservations")}>
          {error && <Alert variant="danger">{error}</Alert>}

          <Row className="mb-4">
            <Col md={6}>
              <Button variant="primary" onClick={fetchReservations}>
                {t("Refresh")}
              </Button>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>{t("FilterStatus")}</Form.Label>
                <Form.Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">{t("AllReservations")}</option>
                  <option value={reservationStatus.PENDING}>
                    {t("Pending")}
                  </option>
                  <option value={reservationStatus.FULFILLED}>
                    {t("Fulfilled")}
                  </option>
                  <option value={reservationStatus.DENIED}>
                    {t("Denied")}
                  </option>
                  <option value={reservationStatus.CANCELLED}>
                    {t("Cancelled")}
                  </option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {loading ? (
            <div className="text-center my-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3">{t("LoadingReservations")}</p>
            </div>
          ) : filteredReservations.length === 0 ? (
            <Alert variant="info">
              {t("NoReservationsFound")}
              {statusFilter !== "all" ? ` ${t("with")} "${statusFilter}"` : ""}.
            </Alert>
          ) : (
            <Table responsive striped bordered hover>
              <thead>
                <tr>
                  <th>{t("Date")}</th>
                  <th>{t("Patient")}</th>
                  <th>{t("Contact")}</th>
                  <th>{t("VisitType")}</th>
                  <th>{t("ScheduledFor")}</th>
                  <th>{t("Location")}</th>
                  <th>{t("Status")}</th>
                  <th>{t("Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {filteredReservations.map((reservation) => (
                  <tr key={reservation.id}>
                    <td>{formatDate(reservation.createdAt?.toDate())}</td>
                    <td>
                      {reservation.name} <br />
                      <small>
                        {t("Age")}: {reservation.age}, {t("Gender")}:{" "}
                        {reservation.gender}
                      </small>
                    </td>
                    <td>{reservation.phone}</td>
                    <td>
                      {reservation.visitType === "urgent" ? (
                        <Badge bg="danger">{t("Urgent")}</Badge>
                      ) : (
                        <Badge bg="info">{t("Scheduled")}</Badge>
                      )}
                    </td>
                    <td>
                      {reservation.visitType === "scheduled"
                        ? `${
                            formatDate(reservation.appointmentDate).split(
                              ","
                            )[0]
                          } at ${reservation.appointmentTime}`
                        : t("AsapText")}
                    </td>
                    {/* <td>
                      {reservation.useCurrentLocation
                        ? t("CurrentLocationGPS")
                        : `${reservation.district}, ${reservation.address}`}
                    </td> */}
                    <td>
                      {reservation.useCurrentLocation ? (
                        reservation.location ? (
                          <a
                            href={`https://www.google.com/maps?q=${reservation.location.latitude},${reservation.location.longitude}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="location-link"
                          >
                            {t("CurrentLocationGPS")}{" "}
                            <i className="bi bi-geo-alt"></i>
                          </a>
                        ) : (
                          t("CurrentLocationGPS")
                        )
                      ) : (
                        `${reservation.district}, ${reservation.address}`
                      )}
                    </td>
                    <td>{getStatusBadge(reservation.status)}</td>
                    <td>
                      <div className="d-flex flex-column gap-2">
                        {reservation.status === reservationStatus.PENDING && (
                          <>
                            <Button
                              variant="success"
                              size="sm"
                              onClick={() =>
                                handleStatusChange(
                                  reservation,
                                  reservationStatus.FULFILLED
                                )
                              }
                            >
                              {t("MarkFulfilled")}
                            </Button>
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() =>
                                handleStatusChange(
                                  reservation,
                                  reservationStatus.DENIED
                                )
                              }
                            >
                              {t("Deny")}
                            </Button>
                          </>
                        )}
                        {reservation.status !== reservationStatus.CANCELLED && (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() =>
                              handleStatusChange(
                                reservation,
                                reservationStatus.CANCELLED
                              )
                            }
                          >
                            {t("Cancel")}
                          </Button>
                        )}
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDeleteReservation(reservation)}
                        >
                          {t("Delete")}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Tab>

        <Tab eventKey="availability" title={t("ManageAvailability")}>
          <AvailabilityManager />
        </Tab>

        <Tab eventKey="diabetes-care" title="رعاية مرضى السكري">
          {error && <Alert variant="danger">{error}</Alert>}

          <Row className="mb-4">
            <Col md={6}>
              <Button
                variant="primary"
                onClick={fetchDiabetesCareRegistrations}
              >
                تحديث البيانات
              </Button>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>تصفية حسب الحالة</Form.Label>
                <Form.Select
                  value={diabetesStatusFilter}
                  onChange={(e) => setDiabetesStatusFilter(e.target.value)}
                >
                  <option value="all">جميع التسجيلات</option>
                  <option value="pending">قيد الانتظار</option>
                  <option value="contacted">تم التواصل</option>
                  <option value="scheduled">تم جدولة الموعد</option>
                  <option value="completed">مكتمل</option>
                  <option value="cancelled">ملغي</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {diabetesCareLoading ? (
            <div className="text-center my-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3">جاري تحميل التسجيلات...</p>
            </div>
          ) : filteredDiabetesCareRegistrations.length === 0 ? (
            <Alert variant="info">
              لا توجد تسجيلات
              {diabetesStatusFilter !== "all"
                ? ` بحالة "${diabetesStatusFilter}"`
                : ""}
              .
            </Alert>
          ) : (
            <Table responsive striped bordered hover>
              <thead>
                <tr>
                  <th>تاريخ التسجيل</th>
                  <th>اسم المريض</th>
                  <th>رقم الهاتف</th>
                  <th>الحالة</th>
                  <th>الإجراءات</th>
                </tr>
              </thead>
              <tbody>
                {filteredDiabetesCareRegistrations.map((registration) => (
                  <tr key={registration.id}>
                    <td>{formatDate(registration.registeredAt?.toDate())}</td>
                    <td>{registration.name}</td>
                    <td>{registration.phone}</td>
                    <td>
                      <Badge
                        bg={
                          registration.status === "pending"
                            ? "warning"
                            : registration.status === "contacted"
                            ? "info"
                            : registration.status === "scheduled"
                            ? "primary"
                            : registration.status === "completed"
                            ? "success"
                            : "secondary"
                        }
                        text={
                          registration.status === "pending" ? "dark" : "white"
                        }
                      >
                        {registration.status === "pending"
                          ? "قيد الانتظار"
                          : registration.status === "contacted"
                          ? "تم التواصل"
                          : registration.status === "scheduled"
                          ? "تم جدولة الموعد"
                          : registration.status === "completed"
                          ? "مكتمل"
                          : registration.status === "cancelled"
                          ? "ملغي"
                          : registration.status}
                      </Badge>
                    </td>
                    <td>
                      {registration.status === "pending" && (
                        <Button
                          variant="outline-info"
                          size="sm"
                          className="me-2"
                          onClick={() =>
                            handleDiabetesCareStatusChange(
                              registration,
                              "contacted"
                            )
                          }
                        >
                          تم التواصل
                        </Button>
                      )}
                      {registration.status === "contacted" && (
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="me-2"
                          onClick={() =>
                            handleDiabetesCareStatusChange(
                              registration,
                              "scheduled"
                            )
                          }
                        >
                          جدولة موعد
                        </Button>
                      )}
                      {(registration.status === "scheduled" ||
                        registration.status === "contacted") && (
                        <Button
                          variant="outline-success"
                          size="sm"
                          className="me-2"
                          onClick={() =>
                            handleDiabetesCareStatusChange(
                              registration,
                              "completed"
                            )
                          }
                        >
                          مكتمل
                        </Button>
                      )}
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() =>
                          handleDeleteDiabetesCareRegistration(registration)
                        }
                      >
                        حذف
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Tab>
      </Tabs>

      {/* Confirmation Modal */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{t("ConfirmAction")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {actionType === "delete" ? (
            <p>
              {t("DeleteConfirmation")}{" "}
              <strong>{selectedReservation?.name}</strong>?
            </p>
          ) : actionType === "diabetes-delete" ? (
            <p>
              هل أنت متأكد من حذف تسجيل{" "}
              <strong>{selectedReservation?.name}</strong>؟
            </p>
          ) : actionType === "diabetes-status-contacted" ? (
            <p>
              تأكيد التواصل مع <strong>{selectedReservation?.name}</strong>؟
            </p>
          ) : actionType === "diabetes-status-scheduled" ? (
            <p>
              تأكيد جدولة موعد لـ <strong>{selectedReservation?.name}</strong>؟
            </p>
          ) : actionType === "diabetes-status-completed" ? (
            <p>
              تأكيد اكتمال الفحص لـ <strong>{selectedReservation?.name}</strong>
              ؟
            </p>
          ) : actionType === `status-${reservationStatus.FULFILLED}` ? (
            <p>
              {t("MarkFulfilled")} <strong>{selectedReservation?.name}</strong>?
            </p>
          ) : actionType === `status-${reservationStatus.DENIED}` ? (
            <p>
              {t("Deny")} <strong>{selectedReservation?.name}</strong>?
            </p>
          ) : actionType === `status-${reservationStatus.CANCELLED}` ? (
            <p>
              {t("Cancel")} <strong>{selectedReservation?.name}</strong>?
            </p>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowConfirmModal(false)}
          >
            {t("Cancel")}
          </Button>
          <Button
            variant={
              actionType === "delete"
                ? "danger"
                : actionType === `status-${reservationStatus.FULFILLED}`
                ? "success"
                : actionType === `status-${reservationStatus.DENIED}`
                ? "danger"
                : "warning"
            }
            onClick={confirmAction}
          >
            {t("Confirm")}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AdminDashboard;
